/**
 * FinanzasConfiguracionPage.jsx — Configuración del Módulo de Finanzas para CIG
 * Ruta: /finanzas/configuracion
 *
 * Incluye gestión de: Entidades, Cuentas, Categorías, Importar/Exportar Excel
 */
import React, { useState, useEffect, useCallback } from 'react';
import finanzasApi from '../../lib/finanzas_api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Building2, Wallet, FolderTree, Plus, Pencil, Trash2, ChevronDown, ChevronRight,
  User, Upload, Download, FileSpreadsheet, AlertCircle
} from 'lucide-react';
import FinAppLayout from '../../components/FinAppLayout';

const currencySymbols = { CLP: '$', USD: 'US$', ARS: 'AR$' };
const CURRENCIES = ['CLP', 'USD', 'ARS'];
const TIPO_LABELS = { ingreso: 'Ingreso', egreso: 'Egreso', inversion: 'Inversión' };
const TIPO_COLORS = {
  ingreso: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  egreso: 'bg-rose-50 text-rose-700 border-rose-100',
  inversion: 'bg-blue-50 text-blue-700 border-blue-100',
};

const TABS = [
  { id: 'entidades', label: 'Entidades', icon: Building2 },
  { id: 'cuentas', label: 'Cuentas', icon: Wallet },
  { id: 'categorias', label: 'Categorías', icon: FolderTree },
  { id: 'intercambio', label: 'Intercambio', icon: FileSpreadsheet },
];

export default function FinanzasConfiguracionPage() {
  const [activeTab, setActiveTab] = useState('entidades');
  const [entidades, setEntidades] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [eRes, cRes, catRes] = await Promise.all([
        finanzasApi.get('/entidades'), finanzasApi.get('/cuentas'), finanzasApi.get('/categorias'),
      ]);
      setEntidades(eRes.data); setCuentas(cRes.data); setCategorias(catRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <FinAppLayout>
      <div data-testid="finanzas-config-page">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Finanzas · Configuración
          </h1>
          <p className="mt-1 text-base text-slate-500">Gestiona entidades, cuentas, categorías e intercambio de datos</p>
        </div>
        <div className="flex gap-1 mb-8 bg-slate-100 p-1 rounded-xl w-fit" data-testid="config-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" strokeWidth={1.5} />{tab.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="space-y-4">{[...Array(3)].map((_,i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div>
        ) : (
          <>
            {activeTab === 'entidades' && <EntidadesTab entidades={entidades} refresh={fetchAll} />}
            {activeTab === 'cuentas' && <CuentasTab cuentas={cuentas} entidades={entidades} refresh={fetchAll} />}
            {activeTab === 'categorias' && <CategoriasTab categorias={categorias} refresh={fetchAll} />}
            {activeTab === 'intercambio' && <IntercambioTab cuentas={cuentas} />}
          </>
        )}
      </div>
    </FinAppLayout>
  );
}

/* ─── Entidades Tab ─── */
function EntidadesTab({ entidades, refresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ nombre: '', tipo: 'Empresa', rut: '', direccion: '', telefono: '' });
  const [saving, setSaving] = useState(false);

  const reset = () => { setForm({ nombre: '', tipo: 'Empresa', rut: '', direccion: '', telefono: '' }); setEditingId(null); };
  const openCreate = () => { reset(); setDialogOpen(true); };
  const openEdit = (e) => { setForm({ nombre: e.nombre, tipo: e.tipo, rut: e.rut||'', direccion: e.direccion||'', telefono: e.telefono||'' }); setEditingId(e.id); setDialogOpen(true); };

  const handleSave = async (ev) => {
    ev.preventDefault();
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      if (editingId) { await finanzasApi.put(`/entidades/${editingId}`, form); toast.success('Actualizada'); }
      else { await finanzasApi.post('/entidades', form); toast.success('Creada'); }
      setDialogOpen(false); reset(); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await finanzasApi.delete(`/entidades/${id}`); toast.success('Eliminada'); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div data-testid="entidades-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Empresas y Personas</h2>
        <Button onClick={openCreate} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl gap-2 h-9 text-sm" data-testid="create-entidad-btn">
          <Plus className="w-3.5 h-3.5" /> Nueva
        </Button>
      </div>
      <div className="space-y-2">
        {entidades.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">Sin entidades</p>
        ) : entidades.map(e => (
          <Card key={e.id} className="border border-slate-100 shadow-sm" data-testid={`ent-card-${e.id}`}>
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${e.tipo==='Empresa'?'bg-blue-50':'bg-violet-50'}`}>
                  {e.tipo==='Empresa' ? <Building2 className="w-4 h-4 text-blue-500" /> : <User className="w-4 h-4 text-violet-500" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">{e.nombre}</p>
                  <p className="text-xs text-slate-400">{e.tipo}{e.rut ? ` · ${e.rut}` : ''}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(e)} className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600" data-testid={`edit-ent-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(e.id)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600" data-testid={`del-ent-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="entidad-dialog">
          <DialogHeader><DialogTitle>{editingId?'Editar':'Nueva'} Entidad</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})} required className="h-10" data-testid="ent-nombre" /></div>
            <div className="space-y-2"><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={v=>setForm({...form,tipo:v})}>
                <SelectTrigger className="h-10" data-testid="ent-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Empresa">Empresa</SelectItem><SelectItem value="Persona">Persona</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>RUT</Label><Input value={form.rut} onChange={e=>setForm({...form,rut:e.target.value})} className="h-10" data-testid="ent-rut" /></div>
              <div className="space-y-2"><Label>Dirección</Label><Input value={form.direccion} onChange={e=>setForm({...form,direccion:e.target.value})} className="h-10" /></div>
              <div className="space-y-2"><Label>Teléfono</Label><Input value={form.telefono} onChange={e=>setForm({...form,telefono:e.target.value})} className="h-10" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setDialogOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl" data-testid="ent-save">{saving?'Guardando...':'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Cuentas Tab ─── */
function CuentasTab({ cuentas, entidades, refresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ nombre: '', moneda_base: 'CLP', moneda_equivalente: 'none', entidad_id: 'none' });
  const [saving, setSaving] = useState(false);

  const reset = () => { setForm({ nombre: '', moneda_base: 'CLP', moneda_equivalente: 'none', entidad_id: 'none' }); setEditingId(null); };
  const openCreate = () => { reset(); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ nombre: c.nombre, moneda_base: c.moneda_base, moneda_equivalente: c.moneda_equivalente||'none', entidad_id: c.entidad_id }); setEditingId(c.id); setDialogOpen(true); };

  const handleSave = async (ev) => {
    ev.preventDefault();
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    if (!form.entidad_id || form.entidad_id === 'none') { toast.error('Selecciona entidad'); return; }
    setSaving(true);
    try {
      const payload = { ...form, moneda_equivalente: form.moneda_equivalente==='none' ? null : form.moneda_equivalente };
      if (editingId) { await finanzasApi.put(`/cuentas/${editingId}`, { nombre: payload.nombre, moneda_equivalente: payload.moneda_equivalente }); toast.success('Actualizada'); }
      else { await finanzasApi.post('/cuentas', payload); toast.success('Creada'); }
      setDialogOpen(false); reset(); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await finanzasApi.delete(`/cuentas/${id}`); toast.success('Eliminada'); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const grouped = entidades.map(ent => ({ ...ent, accounts: cuentas.filter(c => c.entidad_id === ent.id) }));

  return (
    <div data-testid="cuentas-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-700">Cuentas Corrientes</h2>
        <Button onClick={openCreate} disabled={entidades.length===0} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl gap-2 h-9 text-sm" data-testid="create-cuenta-cfg-btn">
          <Plus className="w-3.5 h-3.5" /> Nueva
        </Button>
      </div>
      {grouped.map(ent => (
        <div key={ent.id} className="mb-6" data-testid={`ent-group-${ent.id}`}>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5" /> {ent.nombre}
          </h3>
          {ent.accounts.length === 0 ? (
            <p className="text-xs text-slate-400 pl-6 py-2">Sin cuentas</p>
          ) : (
            <div className="space-y-2 pl-2">
              {ent.accounts.map(c => (
                <Card key={c.id} className="border border-slate-100 shadow-sm" data-testid={`cuenta-card-${c.id}`}>
                  <CardContent className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center"><Wallet className="w-4 h-4 text-indigo-500" /></div>
                      <div>
                        <p className="text-sm font-semibold text-slate-700">{c.nombre}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px]">{currencySymbols[c.moneda_base]} {c.moneda_base}</Badge>
                          {c.moneda_equivalente && <Badge className="bg-slate-50 text-slate-600 border border-slate-200 text-[10px]">Equiv: {c.moneda_equivalente}</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600" data-testid={`edit-cta-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600" data-testid={`del-cta-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ))}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="cuenta-cfg-dialog">
          <DialogHeader><DialogTitle>{editingId?'Editar':'Nueva'} Cuenta</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label>Entidad</Label>
              <Select value={form.entidad_id} onValueChange={v=>setForm({...form,entidad_id:v})} disabled={!!editingId}>
                <SelectTrigger className="h-10" data-testid="cta-entidad"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>Seleccionar entidad</SelectItem>
                  {entidades.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Nombre</Label><Input value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})} required className="h-10" data-testid="cta-nombre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Moneda Base</Label>
                <Select value={form.moneda_base} onValueChange={v=>setForm({...form,moneda_base:v})} disabled={!!editingId}>
                  <SelectTrigger className="h-10" data-testid="cta-moneda"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{currencySymbols[c]} {c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Moneda Equiv.</Label>
                <Select value={form.moneda_equivalente} onValueChange={v=>setForm({...form,moneda_equivalente:v})} disabled={!!editingId}>
                  <SelectTrigger className="h-10" data-testid="cta-equiv"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {CURRENCIES.filter(c=>c!==form.moneda_base).map(c => <SelectItem key={c} value={c}>{currencySymbols[c]} {c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setDialogOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl" data-testid="cta-save">{saving?'Guardando...':'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Categorías Tab ─── */
function CategoriasTab({ categorias, refresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ nombre: '', tipo: 'egreso', parent_id: 'none' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  // Soporte para padre_id y parent_id
  const rootCats = categorias.filter(c => !c.parent_id && !c.padre_id);
  const getChildren = (parentId) => categorias.filter(c => (c.parent_id === parentId) || (c.padre_id === parentId));
  const toggleExpand = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const reset = () => { setForm({ nombre: '', tipo: 'egreso', parent_id: 'none' }); setEditingId(null); };
  const openCreate = (parentId = null, parentTipo = null) => { reset(); setForm(f => ({ ...f, parent_id: parentId||'none', tipo: parentTipo||'egreso' })); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ nombre: c.nombre, tipo: c.tipo, parent_id: c.parent_id||c.padre_id||'none' }); setEditingId(c.id); setDialogOpen(true); };

  const handleSave = async (ev) => {
    ev.preventDefault();
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      const payload = { ...form, parent_id: form.parent_id==='none' ? null : form.parent_id };
      if (editingId) { await finanzasApi.put(`/categorias/${editingId}`, payload); toast.success('Actualizada'); }
      else { await finanzasApi.post('/categorias', payload); toast.success('Creada'); }
      setDialogOpen(false); reset(); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await finanzasApi.delete(`/categorias/${id}`); toast.success('Eliminada'); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  return (
    <div data-testid="categorias-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-700">Árbol de Categorías</h2>
        <Button onClick={() => openCreate()} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl gap-2 h-9 text-sm" data-testid="create-cat-btn">
          <Plus className="w-3.5 h-3.5" /> Nueva Categoría
        </Button>
      </div>
      <div className="space-y-1">
        {rootCats.map(cat => {
          const children = getChildren(cat.id);
          const isExpanded = expanded.has(cat.id);
          const tipoCls = TIPO_COLORS[cat.tipo] || 'bg-slate-50 text-slate-600 border-slate-200';
          return (
            <div key={cat.id} data-testid={`cat-tree-${cat.id}`}>
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group">
                <button onClick={() => toggleExpand(cat.id)} className="p-0.5 rounded hover:bg-slate-200" data-testid={`toggle-cat-${cat.id}`}>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>
                <FolderTree className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <span className="text-sm font-semibold text-slate-700 flex-1">{cat.nombre}</span>
                <Badge className={`${tipoCls} border text-[10px]`}>{TIPO_LABELS[cat.tipo] || cat.tipo}</Badge>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openCreate(cat.id, cat.tipo)} className="p-1 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600" title="Subcategoría" data-testid={`add-sub-${cat.id}`}><Plus className="w-3.5 h-3.5" /></button>
                  <button onClick={() => openEdit(cat)} className="p-1 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600" data-testid={`edit-cat-${cat.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(cat.id)} className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500" data-testid={`del-cat-${cat.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {isExpanded && (
                <div className="ml-8 space-y-0.5">
                  {children.map(child => (
                    <div key={child.id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 group" data-testid={`cat-child-${child.id}`}>
                      <span className="text-slate-300 text-xs">└</span>
                      <span className="text-sm text-slate-600 flex-1">{child.nombre}</span>
                      <Badge className={`${TIPO_COLORS[child.tipo]||''} border text-[10px]`}>{TIPO_LABELS[child.tipo]}</Badge>
                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                        <button onClick={() => openEdit(child)} className="p-1 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600" data-testid={`edit-cat-${child.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(child.id)} className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500" data-testid={`del-cat-${child.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => openCreate(cat.id, cat.tipo)} className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-indigo-500 hover:text-indigo-700 font-medium ml-4" data-testid={`add-child-${cat.id}`}>
                    <Plus className="w-3 h-3" /> Agregar subcategoría
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm rounded-xl" data-testid="cat-dialog">
          <DialogHeader><DialogTitle>{editingId?'Editar':'Nueva'} Categoría</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})} required className="h-10" placeholder="Nombre" data-testid="cat-nombre" /></div>
            <div className="space-y-2"><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={v=>setForm({...form,tipo:v})}>
                <SelectTrigger className="h-10" data-testid="cat-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ingreso">Ingreso</SelectItem>
                  <SelectItem value="egreso">Egreso</SelectItem>
                  <SelectItem value="inversion">Inversión</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Padre (opcional)</Label>
              <Select value={form.parent_id} onValueChange={v=>setForm({...form,parent_id:v})}>
                <SelectTrigger className="h-10" data-testid="cat-parent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Raíz —</SelectItem>
                  {rootCats.filter(c=>c.id!==editingId).map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setDialogOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl" data-testid="cat-save">{saving?'Guardando...':'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Intercambio Tab ─── */
function IntercambioTab({ cuentas }) {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exportCuenta, setExportCuenta] = useState('all');
  const fileInputRef = React.useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = exportCuenta !== 'all' ? `?cuenta_id=${exportCuenta}` : '';
      const response = await finanzasApi.get(`/movimientos/export${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      link.setAttribute('download', cd ? cd.split('filename=')[1] : 'movimientos.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archivo exportado');
    } catch { toast.error('Error al exportar'); }
    finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await finanzasApi.post('/movimientos/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      if (res.data.imported > 0) toast.success(`${res.data.imported} movimientos importados`);
      if (res.data.errors?.length > 0) toast.error(`${res.data.errors.length} errores`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al importar'); }
    finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  return (
    <div data-testid="intercambio-tab" className="space-y-8">
      <Card className="border border-slate-100 shadow-sm rounded-xl">
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Download className="w-4 h-4 text-emerald-500" /> Exportar a Excel</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Descarga todos los movimientos en formato .xlsx.</p>
          <div className="flex items-center gap-3">
            <Select value={exportCuenta} onValueChange={setExportCuenta}>
              <SelectTrigger className="w-[230px] h-9 text-sm" data-testid="export-cuenta-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cuentas</SelectItem>
                {cuentas.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre} ({c.moneda_base})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleExport} disabled={exporting} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl gap-2 h-9 text-sm" data-testid="export-btn">
              <Download className="w-3.5 h-3.5" />{exporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card className="border border-slate-100 shadow-sm rounded-xl">
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Upload className="w-4 h-4 text-indigo-500" /> Importar desde Excel</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Sube un archivo .xlsx para migrar historial desde otro sistema.</p>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Columnas esperadas</p>
            <div className="flex flex-wrap gap-2">
              {['Fecha','Descripción','Ingreso','Egreso','Estado','Cuenta','Moneda','Categoría','Monto Equiv.'].map(col => (
                <span key={col} className="text-[11px] bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-600 font-mono">{col}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={importing} variant="outline" className="rounded-xl gap-2 h-9 text-sm border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="import-btn">
              <Upload className="w-3.5 h-3.5" />{importing ? 'Importando...' : 'Seleccionar Archivo'}
            </Button>
          </div>
          {importResult && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="import-result">
              <div className="flex items-center gap-4">
                <div><span className="text-xs text-slate-400">Total</span><p className="text-lg font-bold text-slate-700">{importResult.total_rows}</p></div>
                <div><span className="text-xs text-slate-400">Importados</span><p className="text-lg font-bold text-emerald-600">{importResult.imported}</p></div>
                <div><span className="text-xs text-slate-400">Errores</span><p className="text-lg font-bold text-rose-600">{importResult.errors?.length || 0}</p></div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-3 max-h-32 overflow-auto">
                  {importResult.errors.slice(0, 10).map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-rose-600 py-1">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>Fila {err.row}: {err.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
